import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { FONT_SIZE, HOLIDAY_GROUPS, INK, NEUTRAL, SHADOW } from "../../../theme/tokens";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { DashedAddButton } from "../../UIToolkit/Clickable/DashedAddButton";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { type SystemChipItem, SystemChips } from "../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EyebrowLabel } from "../../UIToolkit/molecules/EyebrowLabel";
import { SelectionActionBar } from "../../UIToolkit/molecules/SelectionActionBar";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import { CONFIG_TABLE_BODY_CELL_SX, CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { HolidayAddDialog } from "./HolidayAddDialog";
import { HolidayApplyDialog } from "./HolidayApplyDialog";
import { HolidayDeleteDialog } from "./HolidayDeleteDialog";
import { HolidayEditDialog } from "./HolidayEditDialog";
import { HolidayListRow, type HolidayStaffCell } from "./HolidayListRow";
import { HolidaySectionError } from "./HolidaySectionError";
import type { Holiday, HolidayGroup } from "./holidayApi";
import { applyBlockedKey } from "./holidayFormat";
import { useDepartmentToday, useHolidayStaffCounts, useHolidays } from "./holidayQueries";

/** The year picker offers this year and the next two — the range the server lists in one go. */
const YEARS_OFFERED = 3;
const COLUMNS = [
	{ key: "holidays.list.column.holiday", width: "24%" },
	{ key: "holidays.list.column.dateParts", width: "24%" },
	{ key: "holidays.list.column.works", width: "18%" },
	{ key: "holidays.list.column.staff", width: "14%", subKey: "holidays.list.column.staffSub" },
	{ key: "holidays.list.column.status", width: "20%" },
] as const;

type Props = { groups: HolidayGroup[]; canEdit: boolean };

/** The department's holidays for the chosen years, each with the group that works it. */
export const HolidayListSection: FC<Props> = ({ groups, canEdit }) => {
	const { t } = useTranslation();
	const { chosenDepartment, timezone } = useHospitalData();
	const today = useDepartmentToday();
	const years = useMemo(() => Array.from({ length: YEARS_OFFERED }, (_, index) => today.year + index), [today]);
	const [pickedYears, setPickedYears] = useState<Set<number>>(() => new Set([today.year]));
	// After New Year the offered years move on, so a year no longer offered drops out.
	const selectedYears = useMemo(() => {
		const kept = years.filter((year) => pickedYears.has(year));
		// DEFAULT: when only last year was picked, the list moves on to this year, as on a fresh open.
		return new Set(kept.length > 0 ? kept : [today.year]);
	}, [years, pickedYears, today]);
	const yearItems = useMemo<SystemChipItem<number>[]>(
		() => years.map((year) => ({ value: year, label: String(year), ariaLabel: t("holidays.list.yearAria", { year }) })),
		[years, t],
	);
	const selectedYearItems = useMemo(() => yearItems.filter((item) => selectedYears.has(item.value)), [yearItems, selectedYears]);
	// Several years can be on; the last one can't be turned off, so the list always shows something.
	const toggleYear = (item: SystemChipItem<number>, isSelected: boolean) => {
		const next = new Set(selectedYears);
		if (isSelected) next.add(item.value);
		else if (next.size > 1) next.delete(item.value);
		setPickedYears(next);
	};
	const firstYear = Math.min(...selectedYears);
	const lastYear = Math.max(...selectedYears);
	const query = useHolidays(
		chosenDepartment.department_id,
		DateTime.fromObject({ year: firstYear, month: 1, day: 1 }, { zone: timezone }),
		DateTime.fromObject({ year: lastYear, month: 12, day: 31 }, { zone: timezone }),
	);
	const staffCounts = useHolidayStaffCounts(
		chosenDepartment.department_id,
		DateTime.fromObject({ year: firstYear, month: 1, day: 1 }, { zone: timezone }),
		DateTime.fromObject({ year: lastYear, month: 12, day: 31 }, { zone: timezone }),
	);
	const staffCell = (holidayId: string): HolidayStaffCell => {
		if (staffCounts.isError) return "error";
		if (staffCounts.data === undefined) return "loading";
		return staffCounts.data.find((count) => count.holidayId === holidayId)?.jobs;
	};
	const holidays = useMemo(
		() => query.data?.filter((holiday) => selectedYears.has(holiday.date.year)).sort((a, b) => a.date.toMillis() - b.date.toMillis()),
		[query.data, selectedYears],
	);
	const showYears = selectedYears.size > 1;
	const [adding, setAdding] = useState(false);
	const [editing, setEditing] = useState<Holiday | null>(null);
	const [deleting, setDeleting] = useState<Holiday | null>(null);
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
	const [applying, setApplying] = useState(false);
	// Only holidays that can still be applied count, so one applied or started meanwhile drops out.
	// DEFAULT: nothing is selectable until the holidays load.
	const selectable = useMemo(() => (holidays ?? []).filter((holiday) => applyBlockedKey(holiday) === null), [holidays]);
	const chosen = selectable.filter((holiday) => selectedIds.has(holiday.id));
	const allChosen = selectable.length > 0 && chosen.length === selectable.length;
	const select = (holidayId: string, isSelected: boolean) => {
		const next = new Set(selectedIds);
		if (isSelected) next.add(holidayId);
		else next.delete(holidayId);
		setSelectedIds(next);
	};
	// The checkbox column is there only for people who can apply.
	const columnCount = COLUMNS.length + (canEdit ? 2 : 1);

	const renderBody = () => {
		if (query.isError) {
			return (
				<TableRow>
					<TableCell colSpan={columnCount} sx={CONFIG_TABLE_BODY_CELL_SX}>
						<HolidaySectionError onRetry={() => query.refetch()} dataTestId="holiday-list-error" />
					</TableCell>
				</TableRow>
			);
		}
		if (holidays === undefined || holidays.length === 0) {
			return (
				<TableRow>
					<TableCell colSpan={columnCount} sx={[CONFIG_TABLE_BODY_CELL_SX, { py: 3, textAlign: "center", color: INK.MUTED, fontSize: FONT_SIZE.EMPHASIS }]}>
						{holidays === undefined ? <JumpingDots size={6} /> : t("holidays.list.empty")}
					</TableCell>
				</TableRow>
			);
		}
		return holidays.map((holiday, index) => (
			<Fragment key={holiday.id}>
				{showYears && (index === 0 || holidays[index - 1].date.year !== holiday.date.year) && (
					<TableRow data-testid={`holiday-year-row-${holiday.date.year}`}>
						<TableCell colSpan={columnCount} sx={{ py: 0.75, px: 1.5, backgroundColor: NEUTRAL[50], borderBottom: `1px solid ${NEUTRAL[200]}` }}>
							<EyebrowLabel>{holiday.date.year}</EyebrowLabel>
						</TableCell>
					</TableRow>
				)}
				<HolidayListRow
					holiday={holiday}
					groups={groups}
					canEdit={canEdit}
					onEdit={setEditing}
					onDelete={setDeleting}
					staff={staffCell(holiday.id)}
					selected={chosen.includes(holiday)}
					onSelect={(isSelected) => select(holiday.id, isSelected)}
				/>
			</Fragment>
		));
	};

	return (
		<SettingsSection
			title={t("holidays.list.title")}
			icon={<EventOutlinedIcon />}
			description={t("holidays.list.description")}
			action={
				<SystemChips<number>
					items={yearItems}
					selectedItems={selectedYearItems}
					onChange={toggleYear}
					variant="outlined-brand"
					announcePressed
					forceFitContent
					dataTestId="holiday-years"
					getItemDataTestId={(item) => `holiday-year-${item.value}`}
				/>
			}
			layout="stack"
			dataTestId="holiday-list-section"
			sx={{ boxShadow: SHADOW.PANEL_SOFT }}
		>
			<Box sx={CONFIG_TABLE_CONTAINER_SX}>
				{/* Fixed widths, so picking a group or a longer status never shifts the columns. */}
				<Table size="small" data-testid="holiday-list-table" sx={{ tableLayout: "fixed", minWidth: HOLIDAY_GROUPS.LIST_TABLE_MIN_WIDTH_PX }}>
					<TableHead>
						<TableRow>
							{canEdit && (
								<TableCell sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: HOLIDAY_GROUPS.LIST_SELECT_COLUMN_WIDTH_PX }]}>
									<SystemRoundedCheckbox
										checked={allChosen}
										indeterminate={chosen.length > 0 && !allChosen}
										onChange={(isSelected) => setSelectedIds(new Set(isSelected ? selectable.map((holiday) => holiday.id) : []))}
										ariaLabel={t("holidays.apply.selectAllAria")}
										disabled={selectable.length === 0}
										data-testid="holiday-select-all"
									/>
								</TableCell>
							)}
							{COLUMNS.map((column) => (
								<TableCell key={column.key} sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: column.width }]}>
									{t(column.key)}
									{"subKey" in column && <Box sx={{ fontWeight: 400, textTransform: "none" }}>{t(column.subKey)}</Box>}
								</TableCell>
							))}
							<TableCell
								sx={[CONFIG_TABLE_HEADER_CELL_SX, { width: HOLIDAY_GROUPS.LIST_MENU_COLUMN_WIDTH_PX }]}
								aria-label={t("holidays.list.column.actions")}
							/>
						</TableRow>
					</TableHead>
					<TableBody>{renderBody()}</TableBody>
				</Table>
			</Box>
			{canEdit && (
				<Box>
					<DashedAddButton label={t("holidays.add")} onClick={() => setAdding(true)} disabled={false} dataTestId="holiday-add" />
				</Box>
			)}
			{canEdit && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					<SelectionActionBar
						selectedCount={chosen.length}
						countLabel={t("holidays.apply.selected", { count: chosen.length })}
						ariaLabel={t("holidays.apply.barAria")}
						actions={[{ id: "apply", label: t("holidays.apply.button"), buttonVariant: "primary", onClick: () => setApplying(true) }]}
						dataTestId="holiday-apply-bar"
					/>
					<Typography sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY }}>{t("holidays.apply.hint")}</Typography>
				</Box>
			)}
			{applying && <HolidayApplyDialog holidays={chosen} groups={groups} onClose={() => setApplying(false)} onApplied={() => setSelectedIds(new Set())} />}
			{adding && <HolidayAddDialog onClose={() => setAdding(false)} />}
			{editing && <HolidayEditDialog holiday={editing} groups={groups} onClose={() => setEditing(null)} />}
			{deleting && <HolidayDeleteDialog holiday={deleting} groups={groups} onClose={() => setDeleting(null)} />}
		</SettingsSection>
	);
};
